import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { UNIT_TO_MS } from '../constants';
import type { Unit } from '../types';

const MAX_INT32 = 2 ** 31 - 1;

interface UserSpecificProps {
  durationMs: number;
  repeatTimerEnabled: boolean;
  remainingRestartValue: number;
  remainingRestartUnit: Unit;
}

interface PersistentTimer {
  options: UserSpecificProps;
  disabled: boolean;
  id: string;
  onTimerRestart?: () => void;
  editMode?: boolean;
  widgetId?: string;
}

interface StorageTimerData {
  lastStartTime?: number;
}

export function usePersistentTimer({
  disabled,
  editMode = false,
  widgetId = 'widget-id',
  id = 'id',
  onTimerRestart,
  options: {
    durationMs,
    remainingRestartUnit,
    remainingRestartValue,
    repeatTimerEnabled
  }
}: PersistentTimer) {
  const STORAGE_KEY = `user-specific-timer-${widgetId}-${id}`;

  const saveStart = useCallback(
    (start: number) => {
      if (!editMode) {
        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({ lastStartTime: start })
        );
      }
    },
    [STORAGE_KEY, editMode]
  );

  const loadStart = useCallback(() => {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw || editMode) {
      return Date.now();
    }
    try {
      const { lastStartTime = Date.now() }: StorageTimerData = JSON.parse(raw);

      return lastStartTime;
    } catch {
      return Date.now();
    }
  }, [STORAGE_KEY, editMode]);

  // Calculate current timer remaining value
  const computeRemaining = useCallback(
    (start: number) => {
      const now = Date.now();
      const totalElapsed = now - start;

      const pauseMs = repeatTimerEnabled
        ? remainingRestartValue * UNIT_TO_MS[remainingRestartUnit]
        : 0;

      const cycleMs = durationMs + pauseMs;
      const cyclesPassed = repeatTimerEnabled
        ? Math.floor(totalElapsed / cycleMs)
        : 0;

      const offset = totalElapsed - cyclesPassed * cycleMs;
      const remaining = offset < durationMs ? durationMs - offset : 0;

      return { remaining, pauseMs, cycleMs, offset };
    },
    [
      durationMs,
      repeatTimerEnabled,
      remainingRestartValue,
      remainingRestartUnit
    ]
  );

  const [remainingTime, setRemainingTime] = useState(() => {
    const start = loadStart();
    const { remaining } = computeRemaining(start);

    return remaining;
  });
  const repeatTimeoutId = useRef<NodeJS.Timeout>();

  const clearTimer = useCallback(() => {
    if (repeatTimeoutId.current) {
      clearTimeout(repeatTimeoutId.current);
    }
  }, []);

  // Schedule next repeat cycle recursively
  const scheduleNextCycle = useCallback(() => {
    const start = loadStart();
    const { remaining, cycleMs, offset } = computeRemaining(start);
    setRemainingTime(remaining);

    if (!repeatTimerEnabled) {
      return;
    }

    const timeUntilNext = cycleMs - offset;
    const repeatTimeoutInterval =
      timeUntilNext > MAX_INT32 ? MAX_INT32 : timeUntilNext;

    clearTimer();

    repeatTimeoutId.current = setTimeout(() => {
      const newStart = Date.now();
      saveStart(newStart);
      setRemainingTime(durationMs);

      scheduleNextCycle();

      onTimerRestart?.();
    }, repeatTimeoutInterval);
  }, [
    computeRemaining,
    repeatTimerEnabled,
    durationMs,
    onTimerRestart,
    saveStart,
    loadStart,
    clearTimer
  ]);

  // Init timer and repeat cycle
  useLayoutEffect(() => {
    if (disabled) {
      return;
    }

    const initialStart = loadStart();
    saveStart(initialStart);
    scheduleNextCycle();

    return () => {
      clearTimer();
    };
  }, [
    loadStart,
    saveStart,
    scheduleNextCycle,
    durationMs,
    editMode,
    repeatTimerEnabled,
    remainingRestartValue,
    remainingRestartUnit,
    clearTimer,
    disabled
  ]);

  useLayoutEffect(() => {
    if (disabled) {
      return;
    }

    const recalculateRemaining = () => {
      scheduleNextCycle();
    };

    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) recalculateRemaining();
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') recalculateRemaining();
    };

    window.addEventListener('pageshow', onPageShow);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.removeEventListener('pageshow', onPageShow);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [editMode, loadStart, scheduleNextCycle, disabled]);

  return remainingTime;
}
